import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { t } from '../../i18n/nl.js';
import {
  MEAL_TYPE_KEYS, DINNER_COMPONENT_KEYS, getClientProfileFlags, getCalorieGoal,
  getMealLogCounts, monthRangeISO, addMonthsISO, addDaysISO,
} from '../../utils/recipeUtils';

const MEAL_MOMENT_COUNT = 6;

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

async function getRecipes(mealMoment) {
  const { data, error } = await supabase
    .from('recipes')
    .select('*, recipe_meal_moments!inner(meal_moment)')
    .eq('recipe_meal_moments.meal_moment', mealMoment)
    .eq('is_active', true)
    .order('name');
  if (error) throw error;
  return data || [];
}

export default function VoedingPage() {
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();
  const navigate = useNavigate();

  const [flags, setFlags] = useState({ isActive: true, isProspect: false });
  const [maxKcal, setMaxKcal] = useState(2000);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeMeal, setActiveMeal] = useState(null); // { key, label }
  const [recipes, setRecipes] = useState([]);
  const [dinnerRecipes, setDinnerRecipes] = useState({ protein: [], carbs: [], vegetables: [] });
  const [dinnerSel, setDinnerSel] = useState({ protein: null, carbs: null, vegetables: null });
  const [removeMeal, setRemoveMeal] = useState(null);
  const [selectedDate, setSelectedDate] = useState(todayISO());
  const [showCalendar, setShowCalendar] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(todayISO());
  const [mealCounts, setMealCounts] = useState({});
  const [calendarLoading, setCalendarLoading] = useState(false);

  const today = todayISO();
  const isViewingToday = selectedDate === today;
  const isPastDay = selectedDate < today;

  const loadLogs = useCallback(async () => {
    if (!user?.id) return;
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('daily_meal_logs')
        .select(`
          *,
          recipe:recipes!daily_meal_logs_recipe_id_fkey(*),
          dinner_protein:recipes!daily_meal_logs_dinner_protein_id_fkey(*),
          dinner_carbs:recipes!daily_meal_logs_dinner_carbs_id_fkey(*),
          dinner_vegetables:recipes!daily_meal_logs_dinner_vegetables_id_fkey(*)
        `)
        .eq('user_id', user.id)
        .eq('log_date', selectedDate);
      if (error) throw error;
      setLogs(data || []);
    } catch (err) {
      showError(t('recipes.foutOpslaanKeuze') + err.message);
    } finally {
      setLoading(false);
    }
  }, [user?.id, selectedDate]);

  useEffect(() => {
    if (!user?.id) return;
    getClientProfileFlags(user.id).then(setFlags);
    getCalorieGoal(user.id).then(setMaxKcal);
    loadLogs();
  }, [user?.id, loadLogs]);

  function getLog(mealKey) { return logs.find(l => l.meal_type === mealKey); }

  function dateLabel(dateStr) {
    if (dateStr === today) return t('recipes.todayLabel');
    if (dateStr === addDaysISO(today, -1)) return t('recipes.yesterdayLabel');
    return new Date(`${dateStr}T12:00:00`).toLocaleDateString('nl-NL', {
      weekday: 'long', day: 'numeric', month: 'long',
    });
  }

  async function loadCalendarMonth(dateStr) {
    if (!user?.id) return;
    setCalendarLoading(true);
    try {
      const { first, last } = monthRangeISO(dateStr);
      setMealCounts(await getMealLogCounts(user.id, first, last));
    } catch (err) {
      showError(err.message);
    } finally {
      setCalendarLoading(false);
    }
  }

  function openCalendar() {
    setCalendarMonth(selectedDate);
    setShowCalendar(true);
    loadCalendarMonth(selectedDate);
  }

  function goCalendarMonth(delta) {
    const next = addMonthsISO(calendarMonth, delta);
    setCalendarMonth(next);
    loadCalendarMonth(next);
  }

  function handlePickCalendarDate(dateStr) {
    if (dateStr > today) return;
    setSelectedDate(dateStr);
    setShowCalendar(false);
  }

  const totalKcal = logs.reduce((sum, l) => sum + (l.total_calories || 0), 0);
  const remaining = maxKcal - totalKcal;
  const pct = Math.min(100, Math.round((totalKcal / maxKcal) * 100));
  const barColor = pct > 90 ? '#e24b4a' : pct > 75 ? '#f59e0b' : 'var(--color-primary)';

  async function openPicker(meal) {
    setActiveMeal(meal);
    setDinnerSel({ protein: null, carbs: null, vegetables: null });
    if (meal.key === 'dinner') {
      const all = await getRecipes('dinner');
      setDinnerRecipes({
        protein: all.filter(r => r.dinner_component_type === 'protein'),
        carbs: all.filter(r => r.dinner_component_type === 'carbs'),
        vegetables: all.filter(r => r.dinner_component_type === 'vegetables'),
      });
      const existing = getLog('dinner');
      if (existing) {
        setDinnerSel({
          protein: existing.dinner_protein_id || null,
          carbs: existing.dinner_carbs_id || null,
          vegetables: existing.dinner_vegetables_id || null,
        });
      }
    } else {
      setRecipes(await getRecipes(meal.key));
    }
  }

  async function saveMealLog(mealType, recipeId, dinnerProteinId, dinnerCarbsId, dinnerVegetablesId, totalCalories) {
    // Rij-check: bestaat de log-rij al, dan wordt deze upsert een UPDATE en kan
    // hij door RLS stil 0 rijen raken zonder fout (BRIEFING hoofdstuk 8) — het
    // scherm herlaadde dan gewoon de oude keuze zonder melding.
    const { data, error } = await supabase
      .from('daily_meal_logs')
      .upsert({
        user_id: user.id,
        log_date: selectedDate,
        meal_type: mealType,
        recipe_id: recipeId || null,
        dinner_protein_id: dinnerProteinId || null,
        dinner_carbs_id: dinnerCarbsId || null,
        dinner_vegetables_id: dinnerVegetablesId || null,
        total_calories: totalCalories,
        is_planned: false,
        source: 'manual',
        updated_at: new Date().toISOString(),
      }, { onConflict: 'user_id,log_date,meal_type' })
      .select('id')
      .maybeSingle();
    if (error) throw error;
    if (!data) throw new Error(t('common.wijzigingNietOpgeslagen'));
  }

  async function handleSelectRecipe(recipe) {
    try {
      await saveMealLog(activeMeal.key, recipe.id, null, null, null, recipe.calories);
      await loadLogs();
      setActiveMeal(null);
    } catch (err) {
      showError(t('recipes.foutOpslaanKeuze') + err.message);
    }
  }

  async function handleSaveDinner() {
    if (!dinnerSel.protein || !dinnerSel.carbs || !dinnerSel.vegetables) {
      showError(t('recipes.allPartsRequired'));
      return;
    }
    const p = dinnerRecipes.protein.find(r => r.id === dinnerSel.protein);
    const c = dinnerRecipes.carbs.find(r => r.id === dinnerSel.carbs);
    const v = dinnerRecipes.vegetables.find(r => r.id === dinnerSel.vegetables);
    const total = (p?.calories || 0) + (c?.calories || 0) + (v?.calories || 0);
    try {
      await saveMealLog('dinner', null, dinnerSel.protein, dinnerSel.carbs, dinnerSel.vegetables, total);
      await loadLogs();
      setActiveMeal(null);
    } catch (err) {
      showError(t('recipes.foutOpslaanKeuze') + err.message);
    }
  }

  async function confirmRemoveMeal() {
    try {
      const { data: delRows, error: delErr } = await supabase
        .from('daily_meal_logs')
        .delete()
        .eq('user_id', user.id)
        .eq('log_date', selectedDate)
        .eq('meal_type', removeMeal)
        .select('id');
      if (delErr) throw delErr;
      // Verwijderen kan alleen vanaf een bestaande maaltijdkaart, dus 0 rijen
      // betekent hier altijd dat de DELETE niet is doorgekomen.
      if (!delRows?.length) throw new Error(t('common.wijzigingNietVerwijderd'));
      setRemoveMeal(null);
      await loadLogs();
      showSuccess(t('recipes.removeMealAction'));
    } catch (err) {
      showError(t('recipes.foutOpslaanKeuze') + err.message);
    }
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">{t('recipes.voedingTitle')}</h1>
          <p className="page-subtitle">{t('recipes.voedingSubtitle')}</p>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginBottom: 16 }}>
        <button className="btn btn-ghost btn-sm" onClick={() => setSelectedDate(d => addDaysISO(d, -1))} aria-label={t('recipes.previousDay')}>‹</button>
        <span style={{ fontSize: 15, fontWeight: 600, minWidth: 160, textAlign: 'center', textTransform: 'capitalize' }}>{dateLabel(selectedDate)}</span>
        <button className="btn btn-ghost btn-sm" onClick={() => !isViewingToday && setSelectedDate(d => addDaysISO(d, 1))} disabled={isViewingToday} aria-label={t('recipes.nextDay')}>›</button>
        <button className="btn btn-ghost btn-sm" onClick={openCalendar} aria-label={t('recipes.openCalendar')}>📅</button>
      </div>

      {loading ? (
        <div className="loading-center"><div className="spinner" /></div>
      ) : (
      <>
      {!flags.isActive && (
        <div className="error-msg" style={{ marginBottom: 20 }}>{t('recipes.membershipRequiredTekst')}</div>
      )}
      {flags.isActive && flags.isProspect && (
        <div className="card" style={{ marginBottom: 20, borderColor: '#f59e0b' }}>
          <p style={{ margin: 0, fontWeight: 600, color: '#f59e0b' }}>{t('recipes.membershipRequiredTitel')}</p>
          <p className="text-muted" style={{ marginTop: 6, marginBottom: 0 }}>{t('recipes.membershipRequiredTekst')}</p>
        </div>
      )}

      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 10 }}>
          <div>
            <div className="text-muted text-sm">{t('recipes.kcalUsed')}</div>
            <div style={{ fontSize: 18, fontWeight: 700 }}>{totalKcal} kcal</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div className="text-muted text-sm">{t('recipes.kcalGoal')}</div>
            <div style={{ fontSize: 18, fontWeight: 700 }}>{maxKcal} kcal</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div className="text-muted text-sm">{t('recipes.kcalRemaining')}</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: remaining >= 0 ? 'var(--color-primary)' : '#e24b4a' }}>{remaining} kcal</div>
          </div>
        </div>
        <div style={{ height: 8, borderRadius: 4, overflow: 'hidden', background: 'var(--color-bg)' }}>
          <div style={{ height: '100%', borderRadius: 4, width: `${pct}%`, background: barColor }} />
        </div>
        <div className="text-muted text-sm" style={{ textAlign: 'right', marginTop: 4 }}>{t('recipes.kcalPctGoal', { pct })}</div>
      </div>

      <h3 className="text-sm font-semibold" style={{ textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-text-muted)', marginBottom: 8 }}>
        {t('recipes.mealsSectionTitle')}
      </h3>
      <div className="card" style={{ padding: 0, marginBottom: 16 }}>
        {MEAL_TYPE_KEYS.map((meal, i) => {
          const log = getLog(meal.key);
          const isDone = !!log;
          const label = t(`recipes.${meal.label}`);
          let subText = isPastDay ? t('recipes.notFilledIn') : t('recipes.notYetChosen');
          let kcalText = t('recipes.kcalLeftOver', { kcal: remaining });
          if (isDone) {
            if (meal.key === 'dinner' && log.dinner_protein) {
              subText = [log.dinner_protein?.name, log.dinner_carbs?.name, log.dinner_vegetables?.name].filter(Boolean).join(' · ');
            } else {
              subText = log.recipe?.name || '';
            }
            kcalText = (log.total_calories || 0) + ' kcal';
          }
          const disabled = !flags.isActive || flags.isProspect;
          return (
            <div
              key={meal.key}
              style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: 14,
                borderTop: i > 0 ? '1px solid var(--color-border)' : 'none',
                opacity: disabled ? 0.5 : 1,
              }}
            >
              <div
                style={{
                  width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: isDone ? '#e8f5e9' : 'var(--color-bg)', cursor: disabled ? 'default' : 'pointer',
                }}
                onClick={() => {
                  if (disabled) return;
                  if (isDone && meal.key !== 'dinner' && log.recipe?.recipe_type === 'uitgebreid') {
                    navigate(`/client/voeding/recept/${log.recipe.id}?mode=bereiden&logDate=${selectedDate}`);
                    return;
                  }
                  openPicker(meal);
                }}
              >
                {isDone ? '✓' : '+'}
              </div>
              <div style={{ flex: 1, minWidth: 0, cursor: disabled ? 'default' : 'pointer' }}
                onClick={() => {
                  if (disabled) return;
                  if (isDone && meal.key !== 'dinner' && log.recipe?.recipe_type === 'uitgebreid') {
                    navigate(`/client/voeding/recept/${log.recipe.id}?mode=bereiden&logDate=${selectedDate}`);
                    return;
                  }
                  openPicker(meal);
                }}
              >
                <div style={{ fontWeight: 500, fontSize: 14 }}>{label}</div>
                <div className="text-muted text-sm" style={{ fontStyle: isDone ? 'normal' : 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{subText}</div>
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: isDone ? 'var(--color-primary)' : '#f59e0b' }}>{kcalText}</span>
                {isDone && !disabled && (
                  <button className="btn btn-ghost btn-sm" onClick={() => setRemoveMeal(meal.key)} title={t('recipes.removeMealAction')}>✕</button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <button
        className="btn btn-secondary"
        style={{ width: '100%' }}
        disabled={flags.isProspect}
        onClick={() => navigate('/client/voeding/weekschema')}
      >
        {t('recipes.viewWeekschema')}
      </button>

      {activeMeal && (
        <div className="modal-overlay" onClick={() => setActiveMeal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{t(`recipes.${activeMeal.label}`)}</h2>
              <button className="modal-close" onClick={() => setActiveMeal(null)}>✕</button>
            </div>
            {activeMeal.key === 'dinner' ? (
              <DinnerPicker dinnerRecipes={dinnerRecipes} dinnerSel={dinnerSel} setDinnerSel={setDinnerSel} onSave={handleSaveDinner} />
            ) : (
              <RegularPicker
                recipes={recipes}
                remaining={remaining}
                onSelect={handleSelectRecipe}
                onOpenDetail={(r) => navigate(`/client/voeding/recept/${r.id}?mode=kiezen&mealType=${activeMeal.key}&logDate=${selectedDate}`)}
              />
            )}
          </div>
        </div>
      )}

      {removeMeal && (
        <div className="modal-overlay" onClick={() => setRemoveMeal(null)}>
          <div className="modal" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{t('recipes.removeMealTitel')}</h2>
              <button className="modal-close" onClick={() => setRemoveMeal(null)}>✕</button>
            </div>
            <p className="text-muted" style={{ marginBottom: 20 }}>{t('recipes.removeMealTekst')}</p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost" onClick={() => setRemoveMeal(null)}>{t('recipes.annuleren')}</button>
              <button className="btn btn-danger" onClick={confirmRemoveMeal}>{t('recipes.removeMealAction')}</button>
            </div>
          </div>
        </div>
      )}
      </>
      )}

      {showCalendar && (
        <CalendarModal
          month={calendarMonth}
          mealCounts={mealCounts}
          loading={calendarLoading}
          today={today}
          selectedDate={selectedDate}
          onPrevMonth={() => goCalendarMonth(-1)}
          onNextMonth={() => goCalendarMonth(1)}
          onPickDate={handlePickCalendarDate}
          onClose={() => setShowCalendar(false)}
        />
      )}
    </div>
  );
}

function CalendarModal({ month, mealCounts, loading, today, selectedDate, onPrevMonth, onNextMonth, onPickDate, onClose }) {
  const [y, m] = month.split('-').map(Number);
  const monthLabel = new Date(y, m - 1, 1).toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' });
  const firstDow = new Date(y, m - 1, 1).getDay();
  const startOffset = firstDow === 0 ? 6 : firstDow - 1;
  const daysInMonth = new Date(y, m, 0).getDate();
  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ day: d, dateStr: `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}` });
  }
  while (cells.length % 7 !== 0) cells.push(null);
  const rows = [];
  for (let i = 0; i < cells.length; i += 7) rows.push(cells.slice(i, i + 7));
  const dayShort = [0, 1, 2, 3, 4, 5, 6].map(i => t(`recipes.dagKort.${[1, 2, 3, 4, 5, 6, 0][i]}`));

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 400 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">{t('recipes.calendarTitle')}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <button className="btn btn-ghost btn-sm" onClick={onPrevMonth} aria-label={t('recipes.calendarPrevMonth')}>‹</button>
          <span style={{ fontWeight: 600, textTransform: 'capitalize' }}>{monthLabel}</span>
          <button className="btn btn-ghost btn-sm" onClick={onNextMonth} aria-label={t('recipes.calendarNextMonth')}>›</button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', textAlign: 'center' }}>
          {dayShort.map((d, i) => (
            <div key={i} className="text-muted text-sm" style={{ fontWeight: 600, padding: '4px 0' }}>{d}</div>
          ))}
        </div>
        {loading ? (
          <div className="loading-center" style={{ padding: 24 }}><div className="spinner" /></div>
        ) : rows.map((row, ri) => (
          <div key={ri} style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
            {row.map((cell, ci) => {
              if (!cell) return <div key={`e-${ri}-${ci}`} />;
              const isFuture = cell.dateStr > today;
              const isSelected = cell.dateStr === selectedDate;
              const isToday = cell.dateStr === today;
              const filledCount = mealCounts[cell.dateStr] || 0;
              const dotColor = filledCount >= MEAL_MOMENT_COUNT ? '#2e7d32' : filledCount > 0 ? '#f59e0b' : null;
              return (
                <button
                  key={cell.dateStr}
                  onClick={() => onPickDate(cell.dateStr)}
                  disabled={isFuture}
                  style={{
                    border: 'none', background: isSelected ? 'var(--color-primary)' : isToday ? 'rgba(29,158,117,0.15)' : 'transparent',
                    color: isSelected ? '#fff' : isToday ? 'var(--color-primary)' : 'inherit',
                    borderRadius: '50%', width: 32, height: 32, margin: '2px auto', display: 'flex',
                    flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    cursor: isFuture ? 'default' : 'pointer', opacity: isFuture ? 0.3 : 1, fontSize: 13,
                  }}
                >
                  <span>{cell.day}</span>
                  {dotColor && <span style={{ width: 4, height: 4, borderRadius: 2, background: dotColor, marginTop: 1 }} />}
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

function RegularPicker({ recipes, remaining, onSelect, onOpenDetail }) {
  if (recipes.length === 0) {
    return (
      <div className="empty-state">
        <p>{t('recipes.noRecipesAvailable')}</p>
        <p className="text-muted text-sm">{t('recipes.noRecipesSub')}</p>
      </div>
    );
  }
  return (
    <div>
      {recipes.map((r, i) => {
        const tooMuch = r.calories > remaining;
        const isUitgebreid = r.recipe_type === 'uitgebreid';
        return (
          <div
            key={r.id}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
              padding: '12px 4px', borderTop: i > 0 ? '1px solid var(--color-border)' : 'none',
              opacity: tooMuch ? 0.4 : 1, cursor: tooMuch ? 'default' : 'pointer',
            }}
            onClick={() => { if (tooMuch) return; if (isUitgebreid) onOpenDetail(r); else onSelect(r); }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 14 }}>{r.name}</span>
                {isUitgebreid && <span className="badge">{t('recipes.badgeExtended')}</span>}
              </div>
              {r.description && <div className="text-muted text-sm">{r.description}</div>}
              {tooMuch && <div className="text-sm" style={{ color: '#e24b4a' }}>{t('recipes.tooMuchKcal')}</div>}
            </div>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-primary)', flexShrink: 0 }}>{r.calories} kcal</div>
          </div>
        );
      })}
    </div>
  );
}

function DinnerPicker({ dinnerRecipes, dinnerSel, setDinnerSel, onSave }) {
  const allSelected = dinnerSel.protein && dinnerSel.carbs && dinnerSel.vegetables;
  return (
    <div>
      <div className="card" style={{ background: 'rgba(245,158,11,0.08)', borderColor: '#f59e0b', marginBottom: 14 }}>
        <p style={{ margin: 0, fontSize: 13, color: '#b45309' }}>{t('recipes.dinnerHintText')}</p>
      </div>
      {Object.entries(DINNER_COMPONENT_KEYS).map(([type, labelKey]) => (
        <div key={type} style={{ marginBottom: 14 }}>
          <div className="text-sm font-semibold" style={{ textTransform: 'uppercase', letterSpacing: '0.03em', marginBottom: 6, color: 'var(--color-text-muted)' }}>
            {t(`recipes.${labelKey}Label`)}
          </div>
          {(dinnerRecipes[type] || []).length === 0 ? (
            <p className="text-muted text-sm">{t('recipes.noRecipesAvailable')}</p>
          ) : (dinnerRecipes[type] || []).map((r, i) => {
            const selected = dinnerSel[type] === r.id;
            return (
              <div
                key={r.id}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
                  padding: '10px 4px', borderTop: i > 0 ? '1px solid var(--color-border)' : 'none',
                  background: selected ? '#e8f5e9' : 'transparent', cursor: 'pointer',
                }}
                onClick={() => setDinnerSel(prev => ({ ...prev, [type]: r.id }))}
              >
                <span style={{ fontSize: 14, fontWeight: selected ? 600 : 400, color: selected ? '#1b5e20' : 'inherit' }}>{r.name}</span>
                <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-primary)' }}>{r.calories} kcal {selected ? '✓' : ''}</span>
              </div>
            );
          })}
        </div>
      ))}
      {allSelected && (
        <div className="card" style={{ background: 'rgba(29,158,117,0.08)', borderColor: 'var(--color-primary)', marginBottom: 14 }}>
          <p style={{ margin: 0, fontSize: 13, color: '#1b5e20' }}>{t('recipes.allChosen')}</p>
        </div>
      )}
      <button className="btn btn-primary" style={{ width: '100%' }} disabled={!allSelected} onClick={onSave}>
        {t('recipes.saveDinner')}
      </button>
    </div>
  );
}
